import React, { useState, useMemo, useCallback } from 'react';
import {
    Therapist,
    TherapistPricing,
    getTherapistsByPriceRange,
    getTherapistsByExperience,
    getTherapistsByLanguage,

    getAllLanguages,
    getProbonoTherapists
} from '../data/services';
import { X, ChevronDown, ChevronUp, Filter, Clock, Heart } from 'lucide-react';

interface FilterProps {
    therapists: Therapist[];
    onFilterChange: (filteredTherapists: Therapist[]) => void;
}

 

const NEW_MASTER_CONCERNS_LIST = [
    "Trauma", "Grief & Loss", "Romantic Relationships", "Familial Relationships",
    "Friendships & Peer Concerns", "Anxiety & Overthinking", "Depression & Low Mood",
    "Stress & Burnout", "Extreme Moods", "Self-Esteem & Confidence", "Perfectionism",
    "Body Image Concerns", "Identity Exploration", "Gender Identity - related concerns",
    "Sexuality - related concerns", "Executive Functioning Difficulties",
    "Career & Academic Stress", "Life Transitions", "Neurodivergence",
    "Addiction & Coping Behaviors", "Sleep & Somatic Complaints"
];



const PRICE_RANGES: { id: string; value: TherapistPricing['priceRange']; label: string }[] = [
    { id: 'price-1', value: '0-500', label: '₹0 - ₹500' },
    { id: 'price-2', value: '500-1000', label: '₹500 - ₹1000' },
    { id: 'price-3', value: '1000-1500', label: '₹1000 - ₹1500' },
    { id: 'price-4', value: '1500-2000', label: '₹1500 - ₹2000' },
    { id: 'price-5', value: '2000+', label: '₹2000+' }
];

const EXPERIENCE_RANGES: { id: string; value: Therapist['experienceRange']; label: string }[] = [
    { id: 'exp-1', value: 'Less than 1 year', label: 'Less than 1 year' },
    { id: 'exp-2', value: '1-2 years', label: '1-2 years' },
    { id: 'exp-3', value: '3-4 years', label: '3-4 years' },
    { id: 'exp-4', value: '4+ years', label: '4+ years' }
];

const AVAILABILITY_OPTIONS: { id: string; day: string; label: string }[] = [
    { id: 'day-1', day: 'Monday', label: 'Monday' },
    { id: 'day-2', day: 'Tuesday', label: 'Tuesday' },
    { id: 'day-3', day: 'Wednesday', label: 'Wednesday' },
    { id: 'day-4', day: 'Thursday', label: 'Thursday' },
    { id: 'day-5', day: 'Friday', label: 'Friday' },
    { id: 'day-6', day: 'Saturday', label: 'Saturday' },
    { id: 'day-7', day: 'Sunday', label: 'Sunday' },
];

const TIME_SLOTS: { id: string; timeSlot: string; label: string }[] = [
    { id: 'time-1', timeSlot: 'Morning', label: 'Morning' },
    { id: 'time-2', timeSlot: 'Afternoon', label: 'Afternoon' },
    { id: 'time-3', timeSlot: 'Evening', label: 'Evening' },
];


const TherapistFilter: React.FC<FilterProps> = ({ therapists, onFilterChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    //const [selectedConcerns, setSelectedConcerns] = useState<string[]>([]);

    const [selectedDirectConcerns, setSelectedDirectConcerns] = useState<string[]>([]);

    const [selectedPriceRanges, setSelectedPriceRanges] = useState<TherapistPricing['priceRange'][]>([]);
    const [selectedExperienceRanges, setSelectedExperienceRanges] = useState<Therapist['experienceRange'][]>([]);
    const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
    const [showProbono, setShowProbono] = useState<boolean>(false);

    // 
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);



    const allLanguages = getAllLanguages();

    const getTherapistsByAvailability = useCallback((day: string, timeSlot?: string): Therapist[] => {
        return therapists.filter(therapist => {
            // Check if the therapist has any availability slot that matches the criteria
            return therapist.availability.some(slot => {
                const availableDays = slot.days.split(',').map(d => d.trim());
                const availableHours = slot.hours.toLowerCase();

                const dayMatches = availableDays.some(d =>
                    d.toLowerCase() === day.toLowerCase()
                );

                if (!timeSlot) return dayMatches;

                // eslint-disable-next-line
                const timeMatches = timeSlot.toLowerCase() === 'morning' && availableHours.includes('morning') ||
                    // eslint-disable-next-line
                    timeSlot.toLowerCase() === ('afternoon') && availableHours.includes('afternoon') ||
                    // eslint-disable-next-line
                    timeSlot.toLowerCase() === 'evening' && availableHours.includes('evening');

                return dayMatches && timeMatches;
            });
        });
    }, [therapists]);

    // max 5 filters
    const MAX_CONCERN_SELECTIONS = 5;

    const toggleDirectConcern = useCallback((concern: string) => {
        setSelectedDirectConcerns(prev => {
            const isSelected = prev.includes(concern);
            if (isSelected) {
                return prev.filter(s => s !== concern);
            } else {
                if (prev.length < MAX_CONCERN_SELECTIONS) {
                    return [...prev, concern];
                } else {
                    alert(`You can select a maximum of ${MAX_CONCERN_SELECTIONS} concerns`);
                    return prev;

                }
            }
        });
    }, []);


    const applyFilters = useCallback(() => {
        let filtered = [...therapists];

        if (selectedDirectConcerns.length > 0) {
            filtered = filtered.filter(therapist =>

                therapist?.directConcernsTags && selectedDirectConcerns.some(selectedCat =>

                    therapist.directConcernsTags!.includes(selectedCat)
                )
            )

            // const concernFiltered = selectedDirectConcerns.flatMap(concern =>
            //     getTherapistsByConcern(concern)
            // );
            // const uniqueIds = new Set(concernFiltered.map((t: any) => t.id));
            // filtered = filtered.filter(t => uniqueIds.has(t.id));


        }

        if (selectedPriceRanges.length > 0) {
            const priceFiltered = selectedPriceRanges.flatMap(range =>
                getTherapistsByPriceRange(range)
            );
            const uniqueIds = new Set(priceFiltered.map(t => t.id));
            filtered = filtered.filter(t => uniqueIds.has(t.id));
        }

        if (selectedExperienceRanges.length > 0) {
            const experienceFiltered = selectedExperienceRanges.flatMap(range =>
                getTherapistsByExperience(range)
            );
            const uniqueIds = new Set(experienceFiltered.map(t => t.id));
            filtered = filtered.filter(t => uniqueIds.has(t.id));
        }

        if (selectedLanguages.length > 0) {
            const languageFiltered = selectedLanguages.flatMap(language =>
                getTherapistsByLanguage(language)
            );
            const uniqueIds = new Set(languageFiltered.map(t => t.id));
            filtered = filtered.filter(t => uniqueIds.has(t.id));
        }


        if (showProbono) {
            const probonoFiltered = getProbonoTherapists(true);
            const uniqueIds = new Set(probonoFiltered.map(t => t.id));
            filtered = filtered.filter(t => uniqueIds.has(t.id));
        }


        // Apply availability filters
        if (selectedDays.length > 0) {
            let availabilityFiltered: Therapist[] = [];

            // If time slots are selected, filter by both day and time
            if (selectedTimeSlots.length > 0) {
                selectedDays.forEach(day => {
                    selectedTimeSlots.forEach(timeSlot => {
                        const matches = getTherapistsByAvailability(day, timeSlot);
                        availabilityFiltered = [...availabilityFiltered, ...matches];
                    });
                });
            } else {
                // If only days are selected, filter by day
                availabilityFiltered = selectedDays.flatMap(day =>
                    getTherapistsByAvailability(day)
                );
            }

            const uniqueIds = new Set(availabilityFiltered.map(t => t.id));
            filtered = filtered.filter(t => uniqueIds.has(t.id));
        } else if (selectedTimeSlots.length > 0) {
            // If only time slots are selected (no specific days)
            let timeSlotFiltered: Therapist[] = [];

            // Check each therapist against the selected time slots
            filtered.forEach(therapist => {
                const matches = therapist.availability.some(slot => {
                    const availableHours = slot.hours.toLowerCase();
                    return selectedTimeSlots.some(timeSlot =>
                        (timeSlot.toLowerCase() === 'morning' && availableHours.includes('morning')) ||
                        (timeSlot.toLowerCase() === 'afternoon' && availableHours.includes('afternoon')) ||
                        (timeSlot.toLowerCase() === 'evening' && availableHours.includes('evening'))
                    );
                });
                if (matches) {
                    timeSlotFiltered.push(therapist);
                }
            });

            filtered = timeSlotFiltered;
        }
        onFilterChange(filtered);
        // eslint-disable-next-line
    }, [selectedDirectConcerns,
        selectedPriceRanges,
        selectedExperienceRanges,
        selectedLanguages,
        therapists,
        onFilterChange,
        getTherapistsByAvailability,
        showProbono
    ]);

    // const toggleConcern = useCallback((concern: string) => {
    //     setSelectedConcerns(prev =>
    //         prev.includes(concern)
    //             ? prev.filter(s => s !== concern)
    //             : [...prev, concern]
    //     );
    // }, []);

    const togglePriceRange = useCallback((range: TherapistPricing['priceRange']) => {
        setSelectedPriceRanges(prev =>
            prev.includes(range)
                ? prev.filter(r => r !== range)
                : [...prev, range]
        );
    }, []);

    const toggleExperienceRange = useCallback((range: Therapist['experienceRange']) => {
        setSelectedExperienceRanges(prev =>
            prev.includes(range)
                ? prev.filter(r => r !== range)
                : [...prev, range]
        );
    }, []);

    const toggleLanguage = useCallback((language: string) => {
        setSelectedLanguages(prev =>
            prev.includes(language)
                ? prev.filter(l => l !== language)
                : [...prev, language]
        );
    }, []);

    const toggleDay = useCallback((day: string) => {
        setSelectedDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day]
        );
    }, []);

    const toggleTimeSlot = useCallback((timeSlot: string) => {
        setSelectedTimeSlots(prev =>
            prev.includes(timeSlot)
                ? prev.filter(t => t !== timeSlot)
                : [...prev, timeSlot]
        )
    }, [])

    const resetFilters = useCallback(() => {
        setSelectedDirectConcerns([]);
        setSelectedPriceRanges([]);
        setSelectedExperienceRanges([]);
        setSelectedDays([]);
        setSelectedTimeSlots([]);
        setSelectedLanguages([]);
        setShowProbono(false);
    }, []);

    const toggleSection = useCallback((section: string) => {
        setExpandedSection(expandedSection === section ? null : section);
    }, [expandedSection]);

    const activeFiltersCount = useMemo(() =>
        selectedDirectConcerns.length +
        selectedPriceRanges.length +
        selectedExperienceRanges.length +
        selectedLanguages.length +
        selectedDays.length +
        selectedTimeSlots.length +
        (showProbono ? 1 : 0),
        [
            selectedDirectConcerns,
            selectedPriceRanges,
            selectedExperienceRanges,
            selectedLanguages,
            selectedDays,
            selectedTimeSlots,
            showProbono
        ]
    );

    React.useEffect(() => {
        applyFilters();
    }, [applyFilters, selectedDirectConcerns]);

    return (
        <div className="bg-white rounded-lg shadow-md mb-6">
            <div
                className="p-3 flex justify-between items-center cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                role="button"
                aria-expanded={isExpanded}
                aria-label="Toggle filter options"
            >
                <div className="flex items-center">
                    <Filter size={18} className="text-teal-600 mr-2" aria-hidden="true" />
                    <h3 className="font-medium text-gray-800">Filters</h3>
                    {activeFiltersCount > 0 && (
                        <span className="ml-2 bg-teal-600 text-white text-xs rounded-full px-2 py-0.5">
                            {activeFiltersCount}
                        </span>
                    )}
                </div>
                <div className="flex items-center">
                    {activeFiltersCount > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                resetFilters();
                            }}
                            className="text-xs text-teal-600 hover:text-teal-800 mr-3"
                            aria-label="Clear all filters"
                        >
                            Clear All
                        </button>
                    )}
                    {isExpanded ? <ChevronUp size={18} aria-hidden="true" /> : <ChevronDown size={18} aria-hidden="true" />}
                </div>
            </div>

            {!isExpanded && activeFiltersCount > 0 && (
                <div className="px-3 pb-3">
                    <div className="flex flex-wrap gap-2">
                        {selectedDirectConcerns.map(concern => (
                            <button
                                key={concern}
                                className="bg-teal-50 text-teal-700 px-2 py-1 rounded-full text-xs flex items-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDirectConcern(concern);
                                }}
                                aria-label={`Remove ${concern} filter`}
                            >
                                {concern} <X size={14} className="ml-1" aria-hidden="true" />
                            </button>
                        ))}

                        {selectedPriceRanges.map(range => {
                            const rangeObj = PRICE_RANGES.find(r => r.value === range);
                            return rangeObj ? (
                                <button
                                    key={rangeObj.id}
                                    className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs flex items-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        togglePriceRange(range);
                                    }}
                                    aria-label={`Remove ${rangeObj.label} price filter`}
                                >
                                    {rangeObj.label} <X size={14} className="ml-1" aria-hidden="true" />
                                </button>
                            ) : null;
                        })}

                        {selectedExperienceRanges.map(range => {
                            const rangeObj = EXPERIENCE_RANGES.find(r => r.value === range);
                            return rangeObj ? (
                                <button
                                    key={rangeObj.id}
                                    className="bg-purple-50 text-purple-700 px-2 py-1 rounded-full text-xs flex items-center"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExperienceRange(range);
                                    }}
                                    aria-label={`Remove ${rangeObj.label} experience filter`}
                                >
                                    {rangeObj.label} <X size={14} className="ml-1" aria-hidden="true" />
                                </button>
                            ) : null;
                        })}

                        {selectedLanguages.map(lang => (
                            <button
                                key={lang}
                                className="bg-green-50 text-green-700 px-2 py-1 rounded-full text-xs flex items-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLanguage(lang);
                                }}
                                aria-label={`Remove ${lang} language filter`}
                            >
                                {lang} <X size={14} className="ml-1" aria-hidden="true" />
                            </button>
                        ))}

                        {selectedDays.map(day => (
                            <button
                                key={day}
                                className='bg-amber-50 text-amber-700 px-2 py-1 rounded-full text-xs flex items-center'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDay(day);
                                }}
                                aria-label={`Remove ${day} availability filter`}
                            >
                                {day} <X size={14} className="ml-1" aria-hidden="true" />

                            </button>
                        ))}

                        {selectedTimeSlots.map(slot => (
                            <button
                                key={slot}
                                className="bg-orange-50 text-orange-700 px-2 py-1 rounded-full text-xs flex items-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTimeSlot(slot);
                                }}
                                aria-label={`Remove ${slot} time slot filter`}
                            >
                                {slot} <X size={14} className="ml-1" aria-hidden="true" />
                            </button>
                        ))}

                        {showProbono && (
                            <button
                                className="bg-rose-50 text-rose-700 px-2 py-1 rounded-full text-xs flex items-center"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowProbono(false);
                                }}
                                aria-label="Remove pro bono filter"
                            >
                                Pro Bono <X size={14} className="ml-1" aria-hidden="true" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {isExpanded && (
                <div className="px-3 pb-3 border-t border-gray-100">
                    <div className="border-b border-gray-100 py-2">
                        <button
                            className="flex justify-between items-center cursor-pointer py-1 w-full text-left"
                            onClick={() => toggleSection('concerns')}
                            aria-expanded={expandedSection === 'concerns'}
                            aria-controls="concerns-section"
                        >
                            <h4 className="font-medium text-gray-700">
                                Concerns {selectedDirectConcerns.length > 0 && `(${selectedDirectConcerns.length})`}
                            </h4>
                            {expandedSection === 'concerns' ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                        </button>

                        {/* {expandedSection === 'concerns' && (
                            <div id="concerns-section" className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2 max-h-32 overflow-y-auto">
                                {allConcerns.map(concern => (
                                    <div key={concern} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`concern-${concern}`}
                                            checked={selectedConcerns.includes(concern)}
                                            onChange={() => toggleConcern(concern)}
                                            className="mr-1 h-3 w-3 text-teal-600 focus:ring-teal-500"
                                            aria-label={`Select ${concern} concern`}
                                        />
                                        <label htmlFor={`concern-${concern}`} className="text-xs text-gray-700 truncate">
                                            {concern}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )} */}

                        {/*expandedSection === 'concerns' && (
                            <div id="concerns-section" className="mt-2 max-h-48 overflow-y-auto">
                                {Object.entries(CONCERN_CATEGORIES).map(([category, concernsList]) => (
                                    <div key={category} className="mb-3">
                                        <h5 className="text-xs font-semibold text-gray-600 mb-1">{category}</h5>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                            {concernsList.map(concern => {
                                                // Find all raw concerns that map to this normalized concern
                                                const matchingConcerns = allConcerns.filter(
                                                    c => normalizeConcern(c) === concern
                                                );

                                                // Only show if we have matching concerns in our data
                                                return matchingConcerns.length > 0 ? (
                                                    <div key={concern} className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            id={`concern-${concern}`}
                                                            checked={matchingConcerns.some(c => selectedDirectConcerns.includes(c))}
                                                            onChange={() => {
                                                                // Toggle all matching concerns
                                                                const allSelected = matchingConcerns.every(
                                                                    c => selectedDirectConcerns.includes(c)
                                                                );

                                                                if (allSelected) {
                                                                    // Remove all matching concerns
                                                                    setSelectedDirectConcerns(prev =>
                                                                        prev.filter(c => !matchingConcerns.includes(c))
                                                                    );
                                                                } else {
                                                                    // Add all matching concerns that aren't selected
                                                                    setSelectedDirectConcerns(prev => [
                                                                        ...prev,
                                                                        ...matchingConcerns.filter(c => !prev.includes(c))
                                                                    ]);
                                                                }
                                                            }}
                                                            className="mr-1 h-3 w-3 text-teal-600 focus:ring-teal-500"
                                                            aria-label={`Select ${concern} concern`}
                                                        />
                                                        <label htmlFor={`concern-${concern}`} className="text-xs text-gray-700 truncate">
                                                            {concern}
                                                        </label>
                                                    </div>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )*/}

                        {expandedSection === 'concerns' && (
                            <div id="concerns-section" className="mt-2 max-h-60 overflow-y-auto"> {/* Adjusted max-h */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                    {NEW_MASTER_CONCERNS_LIST.map(concern => (
                                        <div key={concern} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={`direct-concern-${concern.replace(/\s/g, '-')}`} // Create unique ID
                                                checked={selectedDirectConcerns.includes(concern)}
                                                onChange={() => toggleDirectConcern(concern)}
                                                className="mr-1 h-3 w-3 text-teal-600 focus:ring-teal-500"
                                                aria-label={`Select ${concern} concern`}
                                                disabled={selectedDirectConcerns.length >= MAX_CONCERN_SELECTIONS && !selectedDirectConcerns.includes(concern)}
                                            />
                                            <label
                                                htmlFor={`direct-concern-${concern.replace(/\s/g, '-')}`}
                                                className={`text-xs text-gray-700 truncate ${selectedDirectConcerns.length >= MAX_CONCERN_SELECTIONS && !selectedDirectConcerns.includes(concern)
                                                    ? 'opacity-50 cursor-not-allowed'
                                                    : ''
                                                    }`}
                                            >
                                                {concern}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {selectedDirectConcerns.length >= MAX_CONCERN_SELECTIONS && (
                                    <p className="text-xs text-red-500 mt-2">
                                        You have reached the maximum of {MAX_CONCERN_SELECTIONS} selections.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="border-b border-gray-100 py-2">
                        <button
                            className="flex justify-between items-center cursor-pointer py-1 w-full text-left"
                            onClick={() => toggleSection('price')}
                            aria-expanded={expandedSection === 'price'}
                            aria-controls="price-section"
                        >
                            <h4 className="font-medium text-gray-700">
                                Price Range {selectedPriceRanges.length > 0 && `(${selectedPriceRanges.length})`}
                            </h4>
                            {expandedSection === 'price' ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                        </button>

                        {expandedSection === 'price' && (
                            <div id="price-section" className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2">
                                {PRICE_RANGES.map(range => (
                                    <div key={range.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={range.id}
                                            checked={selectedPriceRanges.includes(range.value)}
                                            onChange={() => togglePriceRange(range.value)}
                                            className="mr-1 h-3 w-3 text-teal-600 focus:ring-teal-500"
                                            aria-label={`Select ${range.label} price range`}
                                        />
                                        <label htmlFor={range.id} className="text-xs text-gray-700">
                                            {range.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-b border-gray-100 py-2">
                        <button
                            className="flex justify-between items-center cursor-pointer py-1 w-full text-left"
                            onClick={() => toggleSection('experience')}
                            aria-expanded={expandedSection === 'experience'}
                            aria-controls="experience-section"
                        >
                            <h4 className="font-medium text-gray-700">
                                Experience {selectedExperienceRanges.length > 0 && `(${selectedExperienceRanges.length})`}
                            </h4>
                            {expandedSection === 'experience' ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
                        </button>

                        {expandedSection === 'experience' && (
                            <div id="experience-section" className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-2">
                                {EXPERIENCE_RANGES.map(range => (
                                    <div key={range.id} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={range.id}
                                            checked={selectedExperienceRanges.includes(range.value)}
                                            onChange={() => toggleExperienceRange(range.value)}
                                            className="mr-1 h-3 w-3 text-teal-600 focus:ring-teal-500"
                                            aria-label={`Select ${range.label} experience range`}
                                        />
                                        <label htmlFor={range.id} className="text-xs text-gray-700">
                                            {range.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-b border-gray-100 py-2">
                        <button
                            className="flex justify-between items-center cursor-pointer py-1 w-full text-left"
                            onClick={() => toggleSection('availability')}
                            aria-expanded={expandedSection === 'availability'}
                            aria-controls="availability-section"
                        >
                            <h4 className="font-medium text-gray-700 flex items-center">
                                <Clock size={16} className="mr-1 text-amber-600" aria-hidden="true" />
                                Availability {(selectedDays.length > 0 || selectedTimeSlots.length > 0) &&
                                    `(${selectedDays.length + selectedTimeSlots.length})`}
                            </h4>
                            {expandedSection === 'availability' ?
                                <ChevronUp size={16} aria-hidden="true" /> :
                                <ChevronDown size={16} aria-hidden="true" />
                            }
                        </button>

                        {expandedSection === 'availability' && (
                            <div id="availability-section" className="mt-2">
                                <h5 className="text-xs font-medium text-gray-600 mb-1">Days</h5>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mb-3">
                                    {AVAILABILITY_OPTIONS.map(day => (
                                        <div key={day.id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={day.id}
                                                checked={selectedDays.includes(day.day)}
                                                onChange={() => toggleDay(day.day)}
                                                className="mr-1 h-3 w-3 text-amber-600 focus:ring-amber-500"
                                                aria-label={`Select ${day.label} availability`}
                                            />
                                            <label htmlFor={day.id} className="text-xs text-gray-700">
                                                {day.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                <h5 className="text-xs font-medium text-gray-600 mb-1">Time of Day</h5>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                                    {TIME_SLOTS.map(slot => (
                                        <div key={slot.id} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id={slot.id}
                                                checked={selectedTimeSlots.includes(slot.timeSlot)}
                                                onChange={() => toggleTimeSlot(slot.timeSlot)}
                                                className="mr-1 h-3 w-3 text-orange-600 focus:ring-orange-500"
                                                aria-label={`Select ${slot.label} time slot`}
                                            />
                                            <label htmlFor={slot.id} className="text-xs text-gray-700">
                                                {slot.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="border-b border-gray-100 py-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Heart size={16} className="mr-2 text-rose-600" aria-hidden="true" />
                                <h4 className="font-medium text-gray-700">Pro Bono Services</h4>
                            </div>
                            <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                <input
                                    type="checkbox"
                                    id="probono-toggle"
                                    checked={showProbono}
                                    onChange={() => setShowProbono(!showProbono)}
                                    className="sr-only"
                                    aria-label="Show only therapists offering pro bono services"
                                />
                                <label
                                    htmlFor="probono-toggle"
                                    className={`block overflow-hidden h-5 rounded-full cursor-pointer ${showProbono ? 'bg-rose-500' : 'bg-gray-300'
                                        }`}
                                >
                                    <span
                                        className={`block h-5 w-5 rounded-full bg-white transform transition-transform ${showProbono ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    ></span>
                                </label>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Show only therapists offering free sessions</p>
                    </div>

                    <div className="py-2">
                        <h4 className="font-medium text-gray-700 mb-2">Language</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {allLanguages.map(lang => (
                                <div key={lang} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id={`lang-${lang}`}
                                        checked={selectedLanguages.includes(lang)}
                                        onChange={() => toggleLanguage(lang)}
                                        className="mr-2 h-4 w-4 text-teal-600 focus:ring-teal-500"
                                        aria-label={`Select ${lang} language`}
                                    />
                                    <label htmlFor={`lang-${lang}`} className="text-sm text-gray-700">
                                        {lang}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TherapistFilter;
